import { useState, useEffect } from 'react';
import { Download, Edit, Trash2 } from 'lucide-react';
import camService from '../services/camService';
import parkingService from '../services/parkingService';
import '../styles/camsStyle.css';

export default function Camaras() {
  // Estado para la tabla de cámaras
  const [camaras, setCamaras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado para los parqueos disponibles
  const [parkings, setParkings] = useState([]);
  const [loadingParkings, setLoadingParkings] = useState(false);

  // Estado para el formulario de registro/edición
  const [formData, setFormData] = useState({
    idCam: '',
    camInfo: {},
    location: '',
    parkingName: '',
    installationDate: '',
    ip: '',
    port: '80',
    username: 'root',
    password: '',
    boomId: '',
    direction: ''
  });

  // Estado para controlar si estamos editando
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Estado para el término de búsqueda
  const [searchTerm, setSearchTerm] = useState('');

  // Cargar cámaras y parqueos al montar el componente
  useEffect(() => {
    loadCamaras();
    loadParkings();
  }, []);

  // Función para cargar todas las cámaras
  const loadCamaras = async () => {
    try {
      setLoading(true);
      const data = await camService.getAllCams();
      setCamaras(data);
      setError(null);
    } catch (err) {
      console.error('Error al cargar cámaras:', err);
      setError('Error al cargar las cámaras');
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar todos los parqueos
  const loadParkings = async () => {
    try {
      setLoadingParkings(true);
      const data = await parkingService.getAllParkings();
      setParkings(data);
    } catch (err) {
      console.error('Error al cargar parqueos:', err);
      alert('Error al cargar la lista de parqueos. Por favor intente de nuevo.');
    } finally {
      setLoadingParkings(false);
    }
  };

  // Manejar cambios en el formulario
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Limpiar formulario
  // Obtener booms del parqueo seleccionado
  const selectedParkingBooms = parkings.find(p => p.parkingName === formData.parkingName)?.booms || [];

  const clearForm = () => {
    setFormData({
      idCam: '',
      camInfo: {},
      location: '',
      parkingName: '',
      installationDate: '',
      ip: '',
      port: '80',
      username: 'root',
      password: '',
      boomId: '',
      direction: ''
    });
    setIsEditing(false);
    setEditingId(null);
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    try {
      // Validar campos requeridos
      if (!formData.idCam || !formData.location || !formData.parkingName || !formData.installationDate) {
        alert('Por favor complete todos los campos requeridos');
        return;
      }

      // Preparar datos para enviar
      const camData = {
        idCam: formData.idCam,
        camInfo: formData.camInfo,
        location: formData.location,
        parkingName: formData.parkingName,
        installationDate: new Date(formData.installationDate).toISOString(),
        fail: false,
        ip: formData.ip || '',
        port: formData.port ? Number(formData.port) : 80,
        username: formData.username || 'root',
        password: formData.password || '',
        boomId: formData.boomId || '',
        direction: formData.direction || '',
      };

      if (isEditing) {
        // Actualizar cámara existente
        await camService.updateCam(editingId, camData);
        alert('Cámara actualizada exitosamente');
      } else {
        // Crear nueva cámara
        await camService.createCam(camData);
        alert('Cámara creada exitosamente');
      }

      // Recargar lista y limpiar formulario
      await loadCamaras();
      clearForm();
    } catch (err) {
      console.error('Error al guardar cámara:', err);
      alert('Error al guardar la cámara: ' + (err.response?.data?.message || err.message));
    }
  };

  // Manejar edición de cámara
  const handleEdit = async (idCam) => {
    try {
      const cam = await camService.getCamById(idCam);
      setFormData({
        idCam: cam.idCam,
        camInfo: cam.camInfo || {},
        location: cam.location,
        parkingName: cam.parkingName,
        installationDate: cam.installationDate ? new Date(cam.installationDate).toISOString().split('T')[0] : '',
        ip: cam.ip || '',
        port: cam.port ? String(cam.port) : '80',
        username: cam.username || 'root',
        password: cam.password || '',
        boomId: cam.boomId || '',
        direction: cam.direction || '',
      });
      setIsEditing(true);
      setEditingId(idCam);
      // Scroll al formulario para que sea visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error al cargar cámara para editar:', err);
      alert('Error al cargar los datos de la cámara');
    }
  };

  // Manejar eliminación de cámara
  const handleDelete = async (idCam) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta cámara?')) {
      try {
        await camService.deleteCam(idCam);
        alert('Cámara eliminada exitosamente');
        await loadCamaras();
      } catch (err) {
        console.error('Error al eliminar cámara:', err);
        alert('Error al eliminar la cámara: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // Filtrar cámaras por término de búsqueda
  const filteredCamaras = camaras.filter(camara =>
    camara.idCam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    camara.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    camara.parkingName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Formatear fecha para mostrar
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('es-ES');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="cam-main-container">
      {/* Header con título y contador */}
      <div style={{ marginBottom: '24px' }}>
        <h1 className="cam-main-heading">Gestión de Cámaras</h1>
        <p style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>
          Total de cámaras registradas: <strong>{camaras.length}</strong>
          {parkings.length > 0 && ` | Parqueos disponibles: ${parkings.length}`}
        </p>
      </div>

      {/* Mostrar errores */}
      {error && (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          color: '#c33',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Layout de dos columnas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>

        {/* Columna izquierda - Formulario */}
        <div className="cam-panel" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
          <h2 className="cam-section-heading" style={{
            paddingBottom: '12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            {isEditing ? '✏️ Editar Cámara' : '➕ Registrar Nueva Cámara'}
          </h2>

          {/* Formulario en grid 1 columna */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            <div className="cam-input-group">
              <label className="cam-form-label">
                ID Cámara <span style={{ color: '#e74c3c' }}>*</span>
              </label>
              <input
                type="text"
                name="idCam"
                value={formData.idCam}
                onChange={handleFormChange}
                className="cam-form-input"
                placeholder="Ej: CAM001"
                disabled={isEditing}
                style={{
                  backgroundColor: isEditing ? '#f5f5f5' : 'white',
                  cursor: isEditing ? 'not-allowed' : 'text'
                }}
              />
              {isEditing && (
                <p style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                  El ID no se puede modificar
                </p>
              )}
            </div>

            <div className="cam-input-group">
              <label className="cam-form-label">
                Parqueo Asignado <span style={{ color: '#e74c3c' }}>*</span>
              </label>
              {loadingParkings ? (
                <div className="cam-form-input" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  backgroundColor: '#f9f9f9'
                }}>
                  🔄 Cargando parqueos...
                </div>
              ) : parkings.length === 0 ? (
                <div>
                  <select className="cam-form-input" disabled style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    color: '#856404'
                  }}>
                    <option>⚠️ No hay parqueos registrados</option>
                  </select>
                  <div style={{
                    fontSize: '12px',
                    color: '#856404',
                    backgroundColor: '#fff3cd',
                    padding: '8px',
                    borderRadius: '4px',
                    marginTop: '8px',
                    border: '1px solid #ffc107'
                  }}>
                    ⚠️ Debe crear al menos un parqueo en la sección "Gestión de Parqueos" antes de registrar una cámara.
                  </div>
                </div>
              ) : (
                <select
                  name="parkingName"
                  value={formData.parkingName}
                  onChange={handleFormChange}
                  className="cam-form-input"
                  style={{ cursor: 'pointer' }}
                >
                  <option value="">-- Seleccione un parqueo --</option>
                  {parkings.map((parking) => (
                    <option key={parking._id} value={parking.parkingName}>
                      📍 {parking.parkingName} ({parking.capacity} espacios)
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="cam-input-group">
              <label className="cam-form-label">
                Ubicación <span style={{ color: '#e74c3c' }}>*</span>
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleFormChange}
                className="cam-form-input"
                placeholder="Ej: Entrada principal, Planta 2, etc."
              />
            </div>

            <div className="cam-input-group">
              <label className="cam-form-label">
                Fecha de instalación <span style={{ color: '#e74c3c' }}>*</span>
              </label>
              <input
                type="date"
                name="installationDate"
                value={formData.installationDate}
                onChange={handleFormChange}
                className="cam-form-input"
              />
            </div>

            {/* Separador - Conexion de red */}
            <div style={{ borderTop: '2px solid #e74c3c', paddingTop: '16px', marginTop: '8px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '12px' }}>
                🌐 Conexion de Red (Control de Barrera)
              </h3>
            </div>

            <div className="cam-input-group">
              <label className="cam-form-label">Direccion IP</label>
              <input
                type="text"
                name="ip"
                value={formData.ip}
                onChange={handleFormChange}
                className="cam-form-input"
                placeholder="Ej: 192.168.1.100"
              />
            </div>

            <div className="cam-input-group">
              <label className="cam-form-label">Puerto</label>
              <input
                type="number"
                name="port"
                value={formData.port}
                onChange={handleFormChange}
                className="cam-form-input"
                placeholder="80"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="cam-input-group">
                <label className="cam-form-label">Usuario</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleFormChange}
                  className="cam-form-input"
                  placeholder="root"
                />
              </div>
              <div className="cam-input-group">
                <label className="cam-form-label">Contrasena</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  className="cam-form-input"
                  placeholder="***"
                />
              </div>
            </div>

            {/* Separador - Asociacion con aguja */}
            <div style={{ borderTop: '2px solid #e74c3c', paddingTop: '16px', marginTop: '8px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '12px' }}>
                🚧 Aguja/Barrera Asociada
              </h3>
            </div>

            <div className="cam-input-group">
              <label className="cam-form-label">Aguja (Boom)</label>
              {formData.parkingName && selectedParkingBooms.length > 0 ? (
                <select
                  name="boomId"
                  value={formData.boomId}
                  onChange={handleFormChange}
                  className="cam-form-input"
                  style={{ cursor: 'pointer' }}
                >
                  <option value="">-- Sin aguja --</option>
                  {selectedParkingBooms.map((boom) => (
                    <option key={boom.boomId} value={boom.boomId}>
                      Aguja A{boom.boomId} ({boom.location === 'Enter' ? 'Entrada' : 'Salida'})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name="boomId"
                  value={formData.boomId}
                  onChange={handleFormChange}
                  className="cam-form-input"
                  placeholder={formData.parkingName ? 'No hay agujas en este parqueo' : 'Seleccione un parqueo primero'}
                  disabled={!formData.parkingName}
                />
              )}
            </div>

            <div className="cam-input-group">
              <label className="cam-form-label">Direccion</label>
              <select
                name="direction"
                value={formData.direction}
                onChange={handleFormChange}
                className="cam-form-input"
                style={{ cursor: 'pointer' }}
              >
                <option value="">-- Seleccione --</option>
                <option value="Enter">Entrada</option>
                <option value="Exit">Salida</option>
              </select>
            </div>

            {/* Botones */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid #eee'
            }}>
              {isEditing && (
                <button
                  onClick={clearForm}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  ✖ Cancelar
                </button>
              )}
              <button
                onClick={handleSubmit}
                disabled={parkings.length === 0}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: parkings.length === 0 ? '#ccc' : '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: parkings.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  if (parkings.length > 0) e.target.style.backgroundColor = '#c0392b';
                }}
                onMouseOut={(e) => {
                  if (parkings.length > 0) e.target.style.backgroundColor = '#e74c3c';
                }}
              >
                {isEditing ? '💾 Actualizar' : '➕ Guardar Cámara'}
              </button>
            </div>
          </div>
        </div>

        {/* Columna derecha - Tabla de cámaras */}
        <div className="cam-panel">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h2 className="cam-section-heading" style={{ margin: 0 }}>
              📹 Cámaras Registradas
            </h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="🔍 Buscar cámara..."
                className="cam-form-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  minWidth: '250px',
                  margin: 0
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔄</div>
              Cargando cámaras...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="cam-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead className="cam-table-head" style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="cam-table-header" style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>ID</th>
                    <th className="cam-table-header" style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Parqueo</th>
                    <th className="cam-table-header" style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>IP</th>
                    <th className="cam-table-header" style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Aguja</th>
                    <th className="cam-table-header" style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Ubicación</th>
                    <th className="cam-table-header" style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Estado</th>
                    <th className="cam-table-header" style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCamaras.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#999'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                          {searchTerm ? '🔍' : '📹'}
                        </div>
                        {searchTerm ? `No se encontraron cámaras con "${searchTerm}"` : 'No hay cámaras registradas'}
                      </td>
                    </tr>
                  ) : (
                    filteredCamaras.map((camara) => (
                      <tr
                        key={camara.idCam}
                        style={{
                          backgroundColor: 'white',
                          borderBottom: '1px solid #eee',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <td className="cam-table-cell" style={{ padding: '12px', fontWeight: '500' }}>
                          {camara.idCam}
                        </td>
                        <td className="cam-table-cell" style={{ padding: '12px' }}>
                          📍 {camara.parkingName}
                        </td>
                        <td className="cam-table-cell" style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px' }}>
                          {camara.ip ? `${camara.ip}:${camara.port || 80}` : '-'}
                        </td>
                        <td className="cam-table-cell" style={{ padding: '12px' }}>
                          {camara.boomId ? `A${camara.boomId} (${camara.direction === 'Enter' ? 'Entrada' : camara.direction === 'Exit' ? 'Salida' : '-'})` : '-'}
                        </td>
                        <td className="cam-table-cell" style={{ padding: '12px', color: '#666' }}>
                          {camara.location}
                        </td>
                        <td className="cam-table-cell" style={{ padding: '12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: camara.fail ? '#fee' : '#d4edda',
                            color: camara.fail ? '#c33' : '#155724',
                            border: `1px solid ${camara.fail ? '#fcc' : '#c3e6cb'}`
                          }}>
                            {camara.fail ? '🔴 Con fallo' : '🟢 Activa'}
                          </span>
                        </td>
                        <td className="cam-table-cell" style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEdit(camara.idCam)}
                              title="Editar cámara"
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px'
                              }}
                            >
                              <Edit size={14} /> Editar
                            </button>
                            <button
                              onClick={() => handleDelete(camara.idCam)}
                              title="Eliminar cámara"
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '13px'
                              }}
                            >
                              <Trash2 size={14} /> Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}